import React, { useEffect, useState, useContext } from 'react';
import Grid from '@material-ui/core/Grid';
import { InputLabel, Tooltip } from '@material-ui/core';
import AppContext from '../../../App/AppContext';
import { Multiselect, SecondaryButton } from '../../../Core/FormInput';
import { getAllBranch, postStampData, getBranchStamps_GetDetailsById } from '../../../Core/Service/stamp-screen-service';
import '../commonStyle.scss';
import { Backup, Delete } from '@material-ui/icons';
import { Close } from '@material-ui/icons';
import getMultiImgResized from '../../../Core/CommonFunctions/multiImgResizer';

function StampUploadModal(props) {
    const [state, setState] = useState({
        branchOptions: [],
        selectedBranches: [],
        isLoadingDropdowns: true,
        uploadedImages: [],
        isStampDetailsLoading: false
    });
    const fileInputRef = React.useRef(null);
    const { showToast } = useContext(AppContext);

    useEffect(() => {
        const loadBranches = async () => {
            try {
                const res = await getAllBranch();
                if (res.success) {
                    setState((prev) => ({
                        ...prev,
                        branchOptions: res.data.map((branch) => ({
                            id: branch.id,
                            name: branch.branchShortName
                        })),
                        isLoadingDropdowns: false
                    }));
                }
            } catch (error) {
                console.error('Failed to load branches:', error);
                showToast('Error loading branches');
                setState((prev) => ({ ...prev, isLoadingDropdowns: false }));
            }
        };
        loadBranches();
    }, []);

    useEffect(() => {
        const fetchStampDetails = async () => {
            if (props.id) {
                setState((prev) => ({ ...prev, isStampDetailsLoading: true }));
                try {
                    const res = await getBranchStamps_GetDetailsById(props.id.stampID);
                    if (res.success) {
                        const selectedBranches = res.data.branches.map((branch) => ({
                            id: branch.id,
                            name: branch.name
                        }));

                        setState((prev) => ({
                            ...prev,
                            selectedBranches: selectedBranches
                        }));
                    }
                } catch (error) {
                    console.error('Error loading stamp details:', error);
                    showToast('Error loading stamp associations');
                } finally {
                    setState((prev) => ({ ...prev, isStampDetailsLoading: false }));
                }
            }
        };

        if (props.id) fetchStampDetails();
    }, [props.id]);

    const handleImageUpload = (e) => {
        if (!props.id) {
            getMultiImgResized(e.target.files, (urls) => {
                setState((prev) => ({
                    ...prev,
                    uploadedImages: urls
                }));
            });
        }
    };

    const handleBranchChange = (e, selectedItems) => {
        setState((prev) => ({ ...prev, selectedBranches: selectedItems }));
    };

    const handleSubmit = async () => {
        try {
            let payload;

            if (props.id) {
                payload = {
                    StampID: props.id.stampID,
                    BranchIDs: state.selectedBranches.map((b) => parseInt(b.id))
                };
            } else {
                payload = {
                    StampID: null,
                    StampImage: state.uploadedImages[0],
                    BranchIDs: state.selectedBranches.map((b) => parseInt(b.id))
                };
            }

            const res = await postStampData(payload);

            if (res.success) {
                showToast(props.id ? 'Stamp updated' : 'Stamp created');
                props.onClose(true);
            }
        } catch (error) {
            console.error('Submission error:', error);
            showToast('Operation failed');
        }
    };

    const handleRemoveAssociations = async () => {
        try {
            const payload = {
                StampID: props.id.stampID,
                BranchIDs: []
            };

            const res = await postStampData(payload);

            if (res.success) {
                showToast('All branch associations removed');
                props.onClose(true);
            }
        } catch (error) {
            console.error('Deletion failed:', error);
            showToast('Delete failed');
        }
    };

    return (
        <div className="stamp-upload-modal">
            <Grid container spacing={2} className="main-container">
                {/* Always show upload controls in add mode */}
                {!props.id && (
                    <Grid item xs={12}>
                        <div className="upload-controls">
                            <Tooltip title="Upload stamp images">
                                <label className="upload-button">
                                    <Backup color="secondary" />
                                    <input type="file" multiple onChange={handleImageUpload} accept="image/*" style={{ display: 'none' }} ref={fileInputRef} />
                                </label>
                            </Tooltip>

                            {state.uploadedImages.length > 0 && (
                                <Tooltip title="Clear stamp">
                                    <Close
                                        color="secondary"
                                        onClick={() => {
                                            setState((prev) => ({ ...prev, uploadedImages: [] }));
                                            if (fileInputRef.current) {
                                                fileInputRef.current.value = '';
                                            }
                                        }}
                                        style={{ cursor: 'pointer', marginLeft: 10 }}
                                    />
                                </Tooltip>
                            )}
                        </div>
                    </Grid>
                )}

                {/* Show images/preview when they exist */}
                {(props.id || state.uploadedImages.length > 0) && (
                    <Grid item xs={12}>
                        <Grid container spacing={2} style={{ marginTop: 16 }}>
                            {props.id ? (
                                <Grid item xs={4}>
                                    <img
                                        src={props.id.stampImage}
                                        alt="Existing Stamp"
                                        style={{
                                            width: '100%',
                                            height: 150,
                                            objectFit: 'contain',
                                            border: '1px solid #ddd'
                                        }}
                                    />
                                </Grid>
                            ) : (
                                state.uploadedImages.map((img, index) => (
                                    <Grid item xs={4} key={index}>
                                        <img
                                            src={img}
                                            alt={`Stamp ${index + 1}`}
                                            style={{
                                                width: '100%',
                                                height: 150,
                                                objectFit: 'contain',
                                                border: '1px solid #ddd'
                                            }}
                                        />
                                    </Grid>
                                ))
                            )}
                        </Grid>
                    </Grid>
                )}

                {/* Rest of the code remains the same */}
                <Grid item xs={12}>
                    <InputLabel required shrink>
                        Associate with Branches
                    </InputLabel>
                    <Multiselect
                        options={state.branchOptions}
                        value={state.selectedBranches}
                        onChange={handleBranchChange}
                        placeholder="Select branches"
                        loading={state.isLoadingDropdowns || state.isStampDetailsLoading}
                        disabled={state.isLoadingDropdowns || state.isStampDetailsLoading}
                    />
                </Grid>

                <Grid item xs={12}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <SecondaryButton
                            fullWidth
                            onClick={handleSubmit}
                            disabled={props.id ? state.selectedBranches.length === 0 : state.uploadedImages.length === 0 || state.selectedBranches.length === 0}
                        >
                            {props.id ? 'Update' : 'Create Stamps'}
                        </SecondaryButton>
                        {props.id && (
                            <SecondaryButton variant="contained" style={{ color: 'secondary', padding: '5px 30px' }} onClick={handleRemoveAssociations}>
                                <Delete style={{ marginRight: 8 }} />
                                Delete
                            </SecondaryButton>
                        )}
                    </div>
                </Grid>
            </Grid>
        </div>
    );
}

export default StampUploadModal;
